import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BalanceIcon from '@mui/icons-material/Balance';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import BusinessCenterRoundedIcon from '@mui/icons-material/BusinessCenterRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import LoginIcon from '@mui/icons-material/Login';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import { uploadFile } from '../utils/apiUtils';
import BrandWordmark from './BrandWordmark';
import './LandingPage.css';

const INDUSTRY_COPY = {
  legal: {
    title: 'Legal-Grade PDF Redaction',
    description:
      'Protect contracts, filings, discovery packets, and internal legal reviews with a secure workflow built for high-stakes document handling.',
    bullets: [
      'Privilege-first review workflows',
      'Court-ready exports and auditability',
      'Fast processing for large legal batches',
    ],
  },
  healthcare: {
    title: 'Healthcare & Research PDF Redaction',
    description:
      'Protect patient privacy with confidence. Automatically find and redact Protected Health Information from medical records, research data, and clinical trial PDFs while preserving document integrity.',
    bullets: [
      'Automated PHI pattern detection',
      'Secure patient data anonymization',
      'PHI workflows available under executed BAA on supported plans',
    ],
  },
  finance: {
    title: 'Financial Data Protection at Document Speed',
    description:
      'Keep statements, reports, and customer paperwork in compliance-focused workflows built for operations teams and regulated environments.',
    bullets: [
      'PII and account field detection',
      'Structured review before export',
      'Operational fit for enterprise teams',
    ],
  },
  government: {
    title: 'Public Sector Record Redaction',
    description:
      'Balance transparency, disclosure, and protection with controlled release workflows for requests, records, and internal documents.',
    bullets: [
      'FOIA-supportive release workflows',
      'Public record review controls',
      'Sensitive field protection at scale',
    ],
  },
};

const FAQ_ITEMS = [
  {
    question: 'How does Redacto ensure data is not recoverable?',
    answer:
      'Redacto is designed to remove redacted content from supported export layers rather than only overlaying shapes. Results can vary by file type and workflow, so users must review final output before release.',
  },
  {
    question: 'Does your AI learn from my sensitive documents?',
    answer:
      'By default, document content is not used to train Redacto models. If third-party providers are used for specific features, they are contractually restricted from training on customer content.',
  },
  {
    question: 'Is the processing done on the cloud or locally?',
    answer:
      'Supported deployment options include private or on-premise configurations; availability depends on plan and deployment.',
  },
  {
    question: 'Can Redacto detect handwritten signatures or stamps?',
    answer:
      'Signature, stamp, and visual marker detection is supported in many workflows, but performance depends on scan quality, compression, and document structure. Final review is always required.',
  },
  {
    question: 'What happens if a sensitive word is misspelled in the document?',
    answer:
      'Our engine includes Fuzzy Logic capabilities. It looks for visual and linguistic similarities, so even if a name is slightly misspelled, Redacto will flag it for protection.',
  },
  {
    question: 'Does it support multi-language redaction (e.g., Arabic and English)?',
    answer:
      'Redacto supports multilingual workflows, including Arabic and English, with performance varying by document quality, fonts, and layout complexity.',
  },
  {
    question: 'Can we define our own "Sensitive Patterns"?',
    answer:
      'Yes. Users can train the system on the fly by selecting a specific pattern, like a company logo, and Redacto will find and redact all similar instances across the entire document.',
  },
  {
    question: 'How fast is the "Batch Redaction" for large volumes?',
    answer:
      'Batch performance is optimized for speed, and processing times vary by file size, quality, selected checks, and system load.',
  },
  {
    question: "Does Redacto preserve the document's original formatting after redaction?",
    answer:
      'Yes. Our engine is designed to remove sensitive data without altering the layout, fonts, or structural integrity of the PDF, ensuring it remains professional and readable.',
  },
  {
    question: 'Can we integrate Redacto into our existing workflows via API?',
    answer:
      'API access is available on supported plans. Contact partnerships@redacto.net for current plan availability, limits, and onboarding.',
  },
  {
    question: 'Is there an "Audit Trail" for the redaction process?',
    answer:
      'Yes. Redacto can generate detailed logs of every redaction action, who did it, when, and what pattern was used, providing full transparency for compliance audits.',
  },
  {
    question: 'How long are files retained, and how can we delete them?',
    answer:
      'Files are retained only as needed for processing and review. Deletion controls are available in product or by verified request, and supported enterprise plans can configure retention windows.',
  },
  {
    question: 'Do you offer PHI workflows for healthcare documents?',
    answer:
      'PHI workflows are available only under an executed BAA and supported deployment configuration. If a BAA is not in place, healthcare users should avoid uploading PHI.',
  },
];

const TRUST_ITEMS = [
  'Legal operations',
  'Healthcare teams',
  'Financial services',
  'Public sector',
];

const WORKFLOW_STEPS = [
    {
      id: 'intake',
      title: 'Secure Intake',
      detail: 'Uploaded over TLS in production, with controlled storage.',
      points: ['PDF support', 'File validation before processing'],
    },
  {
    id: 'review',
    title: 'Precision Review',
    detail: 'Review each page with clear markers before finalizing redactions.',
    points: ['Human-in-the-loop verification', 'Consistent output across document pages'],
  },
  {
    id: 'export',
    title: 'Confident Export',
    detail: 'Download clean files with audit visibility and practical retention controls.',
    points: ['Action logs for upload/redact/download', 'Delete when your workflow is complete'],
  },
];

function LandingPage({ user, theme = 'dark', toggleTheme }) {
  const HERO_TITLE = 'Professional PDF\nRedaction Tool';
  const HERO_DESCRIPTION = 'Secure, precise, and easy-to-use document redaction for professionals. Protect sensitive information with security-first workflows and a review process that stays easy to trust.';
  const INITIAL_COMPARE_PERCENT = 52;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [activeIndustry, setActiveIndustry] = useState('finance');
  const [activeFaq, setActiveFaq] = useState(0);
  const [activeWorkflow, setActiveWorkflow] = useState(0);
  const [sliderPercent, setSliderPercent] = useState(INITIAL_COMPARE_PERCENT);
  const [isSliderDragging, setIsSliderDragging] = useState(false);
  const [typedTitle, setTypedTitle] = useState('');
  const [typedDescription, setTypedDescription] = useState('');
  const compareRef = useRef(null);

  const isDark = theme === 'dark';
  const maxUploadSize = 25 * 1024 * 1024;
  const acceptedExtensions = ['pdf', 'docx', 'png', 'jpg', 'jpeg'];
  const displayName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.name || user?.login?.split('@')[0] || 'Workspace';

  const validateUpload = (file) => {
    if (!file) {
      return 'Choose a file first.';
    }
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (!acceptedExtensions.includes(extension)) {
      return 'Upload a PDF, DOCX, PNG, JPG, or JPEG file.';
    }
    if (file.size > maxUploadSize) {
      return 'File is too large. Maximum upload size is 25MB.';
    }
    return '';
  };

  const processUpload = async (file) => {
    const validationError = validateUpload(file);
    if (validationError) {
      setUploadError(validationError);
      setUploadSuccess('');
      return;
    }

    setUploadError('');
    setUploadSuccess('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await uploadFile(formData);

      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }

      const fileId = result.data?.file_id || result.data?.fileId || result.data?.id || null;
      setUploadSuccess('Upload complete. Opening the editor...');
      navigate('/app', { state: fileId ? { fileId } : undefined });
    } catch (error) {
      setUploadError(error.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setDragActive(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      processUpload(file);
    }
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      processUpload(file);
    }
    setDragActive(false);
  };

  const clamp = (value, min = 0, max = 100) => Math.min(max, Math.max(min, value));

  const updateSliderPosition = (event) => {
    const rect = compareRef.current?.getBoundingClientRect();
    if (!rect) return;
    const percent = ((event.clientX - rect.left) / rect.width) * 100;
    setSliderPercent(clamp(percent));
  };

  const startSliderDrag = (event) => {
    event.preventDefault();
    setIsSliderDragging(true);
    updateSliderPosition(event);
  };

  const stopSliderDrag = () => {
    setIsSliderDragging(false);
  };

  const handleSliderKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      setSliderPercent((current) => clamp(current - 6));
    }
    if (event.key === 'ArrowRight') {
      setSliderPercent((current) => clamp(current + 6));
    }
  };

  const handlePrimaryAction = () => {
    if (user) {
      navigate('/app');
      return;
    }
    navigate('/login');
  };

  const handleGoogleLogin = () => {
    navigate('/login');
  };

  const handleEmailLogin = () => {
    navigate('/login', { state: { provider: 'email' } });
  };

  useEffect(() => {
    let titleTimer;
    let descTimer;
    let titleIndex = 0;
    let descIndex = 0;
    setTypedTitle('');
    setTypedDescription('');

    const typeTitle = () => {
      if (titleIndex <= HERO_TITLE.length) {
        setTypedTitle(HERO_TITLE.slice(0, titleIndex));
        titleIndex += 1;
        titleTimer = setTimeout(typeTitle, 26);
        return;
      }
      typeDescription();
    };

    const typeDescription = () => {
      if (descIndex <= HERO_DESCRIPTION.length) {
        setTypedDescription(HERO_DESCRIPTION.slice(0, descIndex));
        descIndex += 1;
        descTimer = setTimeout(typeDescription, 12);
      }
    };

    typeTitle();
    return () => {
      clearTimeout(titleTimer);
      clearTimeout(descTimer);
    };
  }, []);

  const industry = INDUSTRY_COPY[activeIndustry];
  const slug = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  return (
    <div className={`landing-page ${isDark ? 'landing-dark' : 'landing-light'}`}>
      <div className="landing-orb landing-orb-left" />
      <div className="landing-orb landing-orb-right" />

      <nav className="landing-nav">
        <div className="nav-container">
          <button className="nav-brand" onClick={() => navigate('/')} type="button">
            <BrandWordmark className="nav-brand-mark" />
          </button>

          <div className="nav-links">
            <a href="#features" className="nav-link">Features</a>
            <a href="#how-it-works" className="nav-link">How It Works</a>
            <a href="#security" className="nav-link">Security</a>
            <a href="#pricing" className="nav-link">Pricing</a>
            <a href="#api" className="nav-link">API / Docs</a>
            <button type="button" className="nav-link nav-link-button" onClick={() => navigate('/security')}>Security</button>
            <a href="#faq" className="nav-link">Q &amp; A</a>
          </div>

          <div className="nav-actions">
            {toggleTheme ? (
              <button
                className="theme-toggle-btn landing-theme-toggle"
                type="button"
                onClick={toggleTheme}
                aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              >
                {isDark ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
              </button>
            ) : null}

            {user ? (
              <button className="nav-user-chip" type="button" onClick={() => navigate('/app')}>
                {displayName}
              </button>
            ) : (
              <>
                <button className="nav-login-btn" type="button" onClick={() => navigate('/login')}>
                  Login
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      <section className="hero-section">
        <div className="hero-grid">
          <div className="hero-copy">
            <div className="hero-eyebrow">Redacto</div>
            <h1 className="hero-title">
              {typedTitle.split('\n')[0] || '\u00A0'}
              <span>{typedTitle.split('\n')[1] || '\u00A0'}</span>
            </h1>
            <p className="hero-description">
              {typedDescription || '\u00A0'}
            </p>

            <div className="hero-pills">
              <span className="hero-pill"><AutoAwesomeRoundedIcon fontSize="inherit" /> Fast Processing</span>
              <span className="hero-pill"><LockOutlinedIcon fontSize="inherit" /> TLS in production, access controls</span>
              <span className="hero-pill"><GppGoodOutlinedIcon fontSize="inherit" /> Reviewable Results</span>
            </div>

          <div className="hero-actions">
            <button
              className="hero-primary-btn hero-primary-gradient"
              type="button"
              onClick={handlePrimaryAction}
            >
              {user ? 'Open Workspace' : 'Sign up for free'}
              <ArrowOutwardRoundedIcon fontSize="small" />
            </button>
            {!user && (
              <div className="hero-secondary-actions">
                <button className="hero-secondary-btn" type="button" onClick={handleGoogleLogin}>
                  <LoginIcon fontSize="small" /> Sign in with Google
                </button>
                <button className="hero-secondary-btn hero-secondary-btn-ghost" type="button" onClick={handleEmailLogin}>
                  <AlternateEmailIcon fontSize="small" /> Sign in with Email
                </button>
              </div>
            )}
            <p className="hero-subtext">Free access during beta — no payment information required to get started.</p>
          </div>
          <div className="scroll-indicator">
            <span>Scroll</span>
            <div className="scroll-indicator-track">
              <span className="scroll-indicator-dot" />
            </div>
          </div>

          </div>

          <div className="hero-panel-wrap">
            <div className="hero-stack">
              <div
                className={`upload-panel ${dragActive ? 'is-dragging' : ''}`}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setDragActive(true);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={(event) => {
                  event.preventDefault();
                  setDragActive(false);
                }}
                onDrop={handleDrop}
              >
                <div className="upload-icon-shell">
                  <UploadFileOutlinedIcon />
                </div>
                <h2>{dragActive ? 'Drop it here!' : 'Drop your contract here'}</h2>
                <p>
                  {dragActive
                    ? 'Release to start secure redaction.'
                    : 'Experience secure redaction with fast processing. Supports PDF, DOCX, and image files.'}
                </p>
                <button
                  className="upload-select-btn"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Select File'}
                </button>
                <input
                  ref={fileInputRef}
                  className="upload-input"
                  type="file"
                  accept=".pdf,.docx,.png,.jpg,.jpeg"
                  onChange={handleFileChange}
                />
                {uploadError ? <p className="upload-error" aria-live="polite">{uploadError}</p> : null}
                {uploadSuccess ? <p className="upload-success" aria-live="polite">{uploadSuccess}</p> : null}
                <div className="gdpr-badge">
                  <span className="gdpr-dot" />
                  Designed for GDPR-aligned workflows
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="content-section">
        <div className="section-shell">
          <header className="section-header">
            <h2>Security by design</h2>
            <p>
              A seamless, highly secure workflow built for modern compliance requirements.
            </p>
          </header>
          <div className="card-grid three-up">
            <article className="feature-card-modern">
              <div className="icon-outline"><ShieldOutlinedIcon /></div>
              <h3>1. Secure Upload</h3>
              <p>Upload PDF documents through a controlled workflow. Files are processed with ownership checks and per-user separation.</p>
            </article>
            <article className="feature-card-modern">
              <div className="icon-outline"><LayersOutlinedIcon /></div>
              <h3>2. AI Analysis</h3>
              <p>Pattern-aware analysis helps teams detect sensitive content faster without turning review into a black box.</p>
            </article>
            <article className="feature-card-modern">
              <div className="icon-outline"><CenterFocusStrongOutlinedIcon /></div>
              <h3>3. Controlled Retention Redaction</h3>
              <p>Export clean files confidently. Files are retained only as needed for processing and review, with deletion controls available.</p>
            </article>
          </div>
        </div>
      </section>

      <section id="trust" className="content-section trust-strip">
        <div className="section-shell trust-shell">
          <div className="trust-left">
            <ShieldOutlinedIcon fontSize="large" />
            <div>
              <h3>How your data is protected</h3>
              <p>Access controls, audit logging, and reviewable controls.</p>
            </div>
          </div>
          <div className="trust-badges">
            {['Supports SOC 2 readiness controls', 'HIPAA-aligned workflows', 'GDPR-aligned workflows'].map((label) => (
              <span key={label} className="trust-badge">
                <CheckCircleOutlineRoundedIcon fontSize="small" />
                {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section id="trust-transparency" className="content-section">
        <div className="section-shell trust-grid">
          <div>
            <p className="eyebrow">Trust &amp; Transparency</p>
            <h2>How your data is protected</h2>
            <p>Clear, simple commitments: access controls, auditability, and practical deletion options.</p>
          </div>
          <div className="trust-pillars">
            <article className="trust-card">
              <div className="trust-icon"><LockOutlinedIcon fontSize="small" /></div>
              <h4>Retention controls</h4>
              <p>Files are retained only as needed for processing and review. Deletion controls and enterprise retention options are available.</p>
            </article>
            <article className="trust-card">
              <div className="trust-icon"><VerifiedOutlinedIcon fontSize="small" /></div>
              <h4>Audit logging</h4>
              <p>Uploads, redactions, and downloads are logged with user + timestamp.</p>
            </article>
            <article className="trust-card">
              <div className="trust-icon"><ShieldOutlinedIcon fontSize="small" /></div>
              <h4>Access controls</h4>
              <p>Access controls, auditability, and reviewable controls.</p>
            </article>
            <div className="trust-badges tight">
              {['Supports SOC 2 readiness controls', 'HIPAA-aligned workflows', 'GDPR-aligned workflows'].map((label) => (
                <span key={label} className="trust-badge">
                  <CheckCircleOutlineRoundedIcon fontSize="small" />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="security" className="content-section alt-surface">
        <div className="section-shell">
          <header className="section-header">
            <h2>Why choose Redacto</h2>
            <p>Built around three pillars: strong security, intelligent efficiency, and precise control.</p>
          </header>
          <div className="card-grid three-up">
            <article className="info-card">
              <div className="icon-badge"><UploadFileOutlinedIcon /></div>
              <h3>Uncompromising Security</h3>
              <p>Your data is uploaded securely. Processing uses controlled retention, review workflows, and explicit deletion controls.</p>
            </article>
            <article className="info-card">
              <div className="icon-badge"><SearchOutlinedIcon /></div>
              <h3>Intelligent Automation</h3>
              <p>Leverage AI to detect common patterns like names, addresses, and account details while keeping humans in the loop.</p>
            </article>
            <article className="info-card">
              <div className="icon-badge"><VerifiedOutlinedIcon /></div>
              <h3>Granular Control</h3>
              <p>Go beyond basic black boxes. Reviewable controls keep redactions precise, readable, and easy to validate before export.</p>
            </article>
          </div>
        </div>
      </section>

      <section id="industry" className="content-section industry-surface">
        <div className="section-shell industry-shell">
          <header className="section-header">
            <h2>Built for modern teams</h2>
            <p>Tailored workflows for high-compliance industries with secure, professional document handling.</p>
          </header>

          <div className="industry-tabs">
            <button className={`industry-tab ${activeIndustry === 'legal' ? 'active' : ''}`} type="button" onClick={() => setActiveIndustry('legal')}>
              <BalanceIcon />
              Legal &amp; Compliance
            </button>
            <button className={`industry-tab ${activeIndustry === 'healthcare' ? 'active' : ''}`} type="button" onClick={() => setActiveIndustry('healthcare')}>
              <LocalHospitalIcon />
              Healthcare &amp; Research
            </button>
            <button className={`industry-tab ${activeIndustry === 'finance' ? 'active' : ''}`} type="button" onClick={() => setActiveIndustry('finance')}>
              <AccountBalanceIcon />
              Financial Services
            </button>
            <button className={`industry-tab ${activeIndustry === 'government' ? 'active' : ''}`} type="button" onClick={() => setActiveIndustry('government')}>
              <ApartmentIcon />
              Government &amp; Public Sector
            </button>
          </div>

          <div className="industry-content">
            <h3>{industry.title}</h3>
            <p>{industry.description}</p>
            <div className="industry-bullets">
              {industry.bullets.map((bullet) => (
                <div key={bullet} className="industry-bullet">
                  <TaskAltOutlinedIcon fontSize="small" />
                  {bullet}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="content-section process-surface">
        <div className="section-shell">
          <header className="section-header">
            <h2>How it works</h2>
            <p>Simple, fast, and secure. Get your documents redacted in three easy steps.</p>
          </header>

          <div className="card-grid three-up step-grid">
            <article className="step-card">
              <div className="step-visual">
                <div className="step-icon">
                  <UploadFileOutlinedIcon fontSize="small" />
                </div>
                <span className="step-label">Step 1</span>
              </div>
              <h3>Upload Your Document</h3>
              <p>Drag and drop your PDF file or click to browse. Your document is uploaded securely and prepared for review.</p>
            </article>
            <article className="step-card">
              <div className="step-visual">
                <div className="step-icon step-icon-blue">
                  <TuneRoundedIcon fontSize="small" />
                </div>
                <span className="step-label">Step 2</span>
              </div>
              <h3>Select &amp; Redact</h3>
              <p>Use precision tools to mark sensitive information. AI can detect patterns, or you can redact manually with exact control.</p>
            </article>
            <article className="step-card">
              <div className="step-visual">
                <div className="step-icon step-icon-rose">
                  <VerifiedOutlinedIcon fontSize="small" />
                </div>
                <span className="step-label">Step 3</span>
              </div>
              <h3>Download &amp; Save</h3>
              <p>Download the redacted document with fast turnaround. Metadata is stripped, and the workflow ends with a clean export.</p>
            </article>
          </div>

          <div className="process-highlight">
            <h3>Lightning-fast processing</h3>
            <p>From upload to download with fast turnaround. The workflow keeps accuracy, reviewability, and document integrity front and center.</p>
          </div>
        </div>
      </section>

      <section className="content-section">
        <div className="section-shell">
          <header className="section-header">
            <h2>Interactive workflow preview</h2>
            <p>Explore the full experience step by step and see how teams move from intake to secure export.</p>
          </header>
          <div className="workflow-panel">
            <div className="workflow-rail" role="tablist" aria-label="Workflow steps">
              {WORKFLOW_STEPS.map((step, index) => {
                const active = index === activeWorkflow;
                return (
                  <button
                    key={step.id}
                    type="button"
                    className={`workflow-step ${active ? 'active' : ''}`}
                    onClick={() => setActiveWorkflow(index)}
                    role="tab"
                    aria-selected={active}
                  >
                    <span className="workflow-step-index">{index + 1}</span>
                    <span>{step.title}</span>
                  </button>
                );
              })}
            </div>
            <article className="workflow-detail" role="tabpanel">
              <h3>{WORKFLOW_STEPS[activeWorkflow].title}</h3>
              <p>{WORKFLOW_STEPS[activeWorkflow].detail}</p>
              <div className="workflow-points">
                {WORKFLOW_STEPS[activeWorkflow].points.map((point) => (
                  <span key={point} className="workflow-point">
                    <TaskAltOutlinedIcon fontSize="small" />
                    {point}
                  </span>
                ))}
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="content-section alt-surface">
        <div className="section-shell">
          <header className="section-header">
            <h2>See the difference clearly</h2>
            <p>Simple side-by-side proof of what is removed and what stays readable.</p>
          </header>
          <div className="compare-card compare-slider-card" ref={compareRef}>
            <div className="compare-topline">
              <span>Original</span>
              <span>Redacted</span>
            </div>
            <div
              className="compare-frame"
              onPointerMove={(event) => {
                if (isSliderDragging) updateSliderPosition(event);
              }}
              onPointerUp={stopSliderDrag}
              onPointerLeave={stopSliderDrag}
            >
              <article className="compare-panel compare-panel-before">
                <h3>Original content</h3>
                <div className="compare-row"><strong>Client Name</strong><span>Sarah Mitchell</span></div>
                <div className="compare-row"><strong>SSN</strong><span>482-13-6621</span></div>
                <div className="compare-row"><strong>Case ID</strong><span>LC-2026-1840</span></div>
                <div className="compare-row"><strong>Address</strong><span>814 Crescent Ave</span></div>
              </article>
              <article className="compare-panel compare-panel-after compare-panel-overlay" style={{ width: `${sliderPercent}%` }}>
                <h3>Redacted output</h3>
                <div className="compare-row"><strong>Client Name</strong><span>█████ ███████</span></div>
                <div className="compare-row"><strong>SSN</strong><span>███-██-████</span></div>
                <div className="compare-row"><strong>Case ID</strong><span>LC-2026-1840</span></div>
                <div className="compare-row"><strong>Address</strong><span>███ ████████ ███</span></div>
              </article>
              <button
                type="button"
                role="slider"
                tabIndex={0}
                className="compare-slider-handle"
                onPointerDown={startSliderDrag}
                onKeyDown={handleSliderKeyDown}
                style={{ left: `${sliderPercent}%` }}
                aria-label="Reveal redacted content"
                aria-valuenow={Math.round(sliderPercent)}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <span />
              </button>
            </div>
            <p className="compare-note">Drag the handle to reveal how Redacto preserves context while removing sensitive content.</p>
          </div>
        </div>
      </section>
<section id="pricing" className="content-section pricing-surface">
        <div className="section-shell">
          <header className="section-header">
            <h2>Simple B2B pricing</h2>
            <p>Get started with a free tier, then upgrade to a custom enterprise plan designed for secure teams.</p>
          </header>

          <div className="pricing-grid">
            <article className="price-card primary">
              <div className="price-head">
                <div>
                  <h3>Free Trial</h3>
                  <p className="price-desc">Start with secure redaction, audit-ready output, and a low-commitment trial.</p>
                </div>
                <div className="price-tag">Free</div>
              </div>
              <ul className="price-points">
                <li>Up to 5 files per month</li>
                <li>Secure PDF and image redaction</li>
                <li>AI-assisted review checks</li>
                <li>Email support for setup</li>
              </ul>
              <button className="price-cta" type="button" onClick={handlePrimaryAction}>
                Start free trial
              </button>
            </article>

            <article className="price-card outline">
              <div className="price-head">
                <div>
                  <h3>Enterprise</h3>
                  <p className="price-desc">Custom deployments, BAA-enabled workflows, SOC 2 readiness, and priority onboarding.</p>
                </div>
                <div className="price-tag">Contact Us</div>
              </div>
              <ul className="price-points">
                <li>Custom security reviews</li>
                <li>Dedicated support & SLA</li>
                <li>Volume pricing and API access</li>
                <li>BAA / HIPAA-ready workflows</li>
              </ul>
              <button className="price-cta price-cta-ghost" type="button" onClick={() => navigate('/contact')}>
                Contact sales
              </button>
            </article>
          </div>
        </div>
      </section>

      <section id="social-proof" className="content-section">
        <div className="section-shell">
          <header className="section-header">
            <span className="trust-badge">Currently in free beta</span>
            <h2>Built for high-compliance teams</h2>
            <p>Designed for redaction workflows in legal, healthcare, finance, and public-sector environments.</p>
          </header>

          <div className="trust-marquee social-proof-badges">
            {['SOC 2 readiness', 'HIPAA-aligned workflows', 'GDPR-ready handling', 'Audit logging support'].map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
        </div>
      </section>

      <section id="api" className="content-section api-surface">
        <div className="section-shell api-shell">
          <div className="api-panel">
            <div>
              <p className="eyebrow">API / Documentation</p>
              <h2>Integrate with enterprise workflows</h2>
              <p>Technical teams can connect Redacto into existing pipelines, build automated redaction workflows, and validate output through API-driven review.</p>
            </div>
            <a className="api-docs-link" href="#api">
              View API docs
              <ArrowOutwardRoundedIcon fontSize="small" />
            </a>
          </div>
        </div>
      </section>

      <section className="content-section">
        <div className="section-shell">
          <div className="experience-grid">
            <article className="experience-card">
              <div className="experience-icon"><TuneRoundedIcon /></div>
              <h3>Less friction, more clarity</h3>
              <p>Focused actions, clearer status feedback, and guided steps reduce review fatigue for busy teams.</p>
            </article>
            <article className="experience-card">
              <div className="experience-icon"><VisibilityOffRoundedIcon /></div>
              <h3>Safer sharing outcomes</h3>
              <p>Confidence checks and reviewable output help avoid accidental disclosure before files leave your org.</p>
            </article>
            <article className="experience-card">
              <div className="experience-icon"><BoltRoundedIcon /></div>
              <h3>Faster daily operations</h3>
              <p>Reusable patterns and structured workflows help teams handle repeat redaction work in less time.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="content-section">
        <div className="section-shell">
          <div className="trust-marquee">
            {TRUST_ITEMS.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
          <p className="small-note">*PHI workflows require an executed BAA and supported deployment configuration.</p>

          <div className="cta-panel cta-panel-wide">
            <div className="cta-copy">
              <span className="cta-kicker">Enterprise</span>
              <h2>Ready to scale?</h2>
              <p>We&apos;re a hungry startup with enterprise-grade solutions. Let&apos;s build something incredible together.</p>
            </div>
            <div className="cta-side">
              <button className="hero-secondary-btn" type="button" onClick={() => navigate('/contact')}>
                <BusinessCenterRoundedIcon fontSize="small" />
                partnerships@redacto.net
              </button>
              <button className="hero-secondary-btn" type="button" onClick={() => navigate('/support')}>
                <PublicRoundedIcon fontSize="small" />
                Enterprise Hotline
              </button>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="content-section faq-surface">
        <div className="section-shell faq-shell">
          <header className="section-header">
            <h2>Technical FAQ</h2>
            <p>Clear answers for privacy, compliance, deployment, and enterprise workflow needs.</p>
          </header>
          <div className="faq-layout">
            <aside className="faq-assurance-card">
              <div className="faq-highlight-label">At a glance</div>
              <ul className="faq-assurance-points">
                <li>Human review before export</li>
                <li>Audit logging</li>
                <li>Responds within 24 hours</li>
              </ul>
              <div className="faq-assurance-links">
                <button type="button" onClick={() => navigate('/privacy-policy')}>Privacy Policy</button>
                <button type="button" onClick={() => navigate('/terms')}>Terms of Service</button>
              </div>
            </aside>
            <div className="faq-highlight-card">
              <div className="faq-highlight-label">Featured Answer</div>
              <h3>{activeFaq >= 0 ? FAQ_ITEMS[activeFaq].question : 'Select a question'}</h3>
              <p>
                {activeFaq >= 0
                  ? FAQ_ITEMS[activeFaq].answer
                  : 'Pick any question below to explore how Redacto handles privacy, deployment, and compliance.'}
              </p>
            </div>

            <div className="faq-list">
              {FAQ_ITEMS.map((item, index) => {
                const isOpen = activeFaq === index;
                const id = slug(item.question);

                return (
                  <article className={`faq-item ${isOpen ? 'is-open' : ''}`} key={item.question} id={id}>
                    <button
                      className="faq-trigger"
                      type="button"
                      onClick={() => setActiveFaq((current) => (current === index ? -1 : index))}
                      aria-expanded={isOpen}
                      aria-controls={`${id}-answer`}
                    >
                      <span className="faq-question-text">{item.question}</span>
                      <span className="faq-icon-shell">
                        {isOpen ? <RemoveRoundedIcon fontSize="small" /> : <AddRoundedIcon fontSize="small" />}
                      </span>
                    </button>
                    <div className={`faq-answer-wrap ${isOpen ? 'is-open' : ''}`} id={`${id}-answer`}>
                      <p>{item.answer}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="section-shell landing-footer-shell">
          <div className="footer-brand">
            <BrandWordmark className="nav-brand-mark" />
          </div>
          <p className="footer-copy">Secure document redaction for teams that need clean workflows, trustworthy controls, and modern compliance signals.</p>
          <div className="footer-links">
            <button type="button" onClick={() => navigate('/privacy-policy')}>
              Privacy
              <KeyboardArrowRightRoundedIcon fontSize="small" />
            </button>
            <button type="button" onClick={() => navigate('/terms')}>
              Terms
              <KeyboardArrowRightRoundedIcon fontSize="small" />
            </button>
            <button type="button" onClick={() => navigate('/security')}>
              Security
              <KeyboardArrowRightRoundedIcon fontSize="small" />
            </button>
            <button type="button" onClick={() => navigate('/support')}>
              Support
              <KeyboardArrowRightRoundedIcon fontSize="small" />
            </button>
            <button type="button" onClick={() => navigate('/contact')}>
              Contact
              <KeyboardArrowRightRoundedIcon fontSize="small" />
            </button>
            <button type="button" onClick={() => navigate('/user-agreement')}>
              User Agreement
              <KeyboardArrowRightRoundedIcon fontSize="small" />
            </button>
            <a href="#trust-transparency" className="footer-link-inline">
              How your data is protected
              <KeyboardArrowRightRoundedIcon fontSize="small" />
            </a>
          </div>
          
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
